"use client";

import { useRef, useState, useCallback, useEffect } from "react";
import { ArrowLeftRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Report() {
  const [position, setPosition] = useState(50);
  const [imageError, setImageError] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: panelRef,
    isVisible: panelVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: statsRef,
    isVisible: statsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const updatePosition = useCallback((clientX: number) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, x / rect.width * 100));
    setPosition(pct);
  }, []);
  const handlePointerDown = (e: React.PointerEvent) => {
    isDraggingRef.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    updatePosition(e.clientX);
  };
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDraggingRef.current) return;
    updatePosition(e.clientX);
  };
  const handlePointerUp = (e: React.PointerEvent) => {
    isDraggingRef.current = false;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {}
  };
  useEffect(() => {
    const onUp = () => {
      isDraggingRef.current = false;
    };
    window.addEventListener("pointerup", onUp);
    return () => window.removeEventListener("pointerup", onUp);
  }, []);
  const stats = [{
    value: "100% TRACCIABILE",
    label: "ISPEZIONE VIDEO TECNICA"
  }, {
    value: "REPORT DIGITALE",
    label: "DOCUMENTAZIONE TECNICA"
  }, {
    value: "VERIFICA TECNICA",
    label: "QUALITÀ ARIA TECNICA"
  }];
  const handleImageError = () => {
    setImageError(true);
  };
  return <section data-section-id="1205" id="il-report-fotografico-finale" className="bg-background text-foreground pt-36 pb-24 md:pt-44 md:pb-36 px-6 md:px-10 lg:px-16 relative overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        <div className="mb-12 md:mb-20 flex items-center gap-3">
          <span className="font-mono text-[12px] tracking-[0.18em] uppercase text-foreground" style={{
          fontWeight: 500
        }}>
            [SYSTEM_REPORT_01] — DOCUMENTAZIONE TECNICA
          </span>
          <span className="block w-6 h-[3px] bg-foreground" />
        </div>

        <div ref={headerRef} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-24 transition-all duration-[560ms] ease-out" style={{
        opacity: headerVisible ? 1 : 0,
        transform: headerVisible ? "translateY(0)" : "translateY(20px)",
        transitionTimingFunction: "cubic-bezier(0.2, 0.7, 0.2, 1)"
      }}>
          <div className="lg:col-span-5">
            <div className="font-mono text-xs md:text-sm tracking-[0.18em] uppercase text-primary mb-6 font-medium">
              TRASPARENZA OPERATIVA
            </div>
            <h2 className="font-default font-medium uppercase leading-[1.1] tracking-tighter text-secondary" style={{
            fontSize: "clamp(2rem, 4vw, 3rem)"
          }}>
              VERIFICA{" "}
              <span className="text-secondary">
                L'EFFICACIA
              </span>
              .
            </h2>
          </div>

          <div className="lg:col-span-7 flex items-end">
            <p className="font-default text-primary" style={{
            fontSize: "clamp(1rem, 1.25vw, 1.25rem)",
            lineHeight: 1.6
          }}>
              Ogni intervento di riqualificazione igienico-funzionale è supportato da una documentazione tecnica trasparente e incontestabile. Attraverso ispezioni video ad alta risoluzione e una validazione certificata post-intervento, garantiamo la massima tracciabilità dei processi. La nostra metodologia fornisce un quadro tecnico autorevole, assicurando che ogni componente dell'impianto rispetti i più rigorosi standard di efficienza e sicurezza operativa.
            </p>
          </div>
        </div>

        <div ref={panelRef} className="relative transition-all duration-[560ms] ease-out" style={{
        opacity: panelVisible ? 1 : 0,
        transform: panelVisible ? "translateY(0)" : "translateY(20px)",
        transitionTimingFunction: "cubic-bezier(0.2, 0.7, 0.2, 1)"
      }}>
          <div aria-hidden="true" className="absolute -top-4 -left-4 w-full h-full pointer-events-none" style={{
          backgroundImage: "radial-gradient(var(--primary) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
          opacity: 0.15
        }} />

          <div ref={containerRef} className="relative border border-primary/30 shadow-lg rounded-lg overflow-hidden select-none touch-none bg-card" style={{
          aspectRatio: "16 / 9"
        }} onPointerDown={!imageError ? handlePointerDown : undefined} onPointerMove={!imageError ? handlePointerMove : undefined} onPointerUp={!imageError ? handlePointerUp : undefined}>
            {imageError ? <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/professional_inspection_scene.png" alt="Professional HVAC Inspection Scene" className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" /> : <>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/condotto_pulito_certificato_dopo.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/condotto_pulito_certificato_dopo.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/condotto_pulito_certificato_dopo.png-wvc-sizes" alt="After HVAC Duct Inspection" className="absolute inset-0 w-full h-full object-cover pointer-events-none" loading="lazy" decoding="async" draggable={false} onError={handleImageError} />

                <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{
              width: `${position}%`
            }}>
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/condotto_sporco_prima_ispezione.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/condotto_sporco_prima_ispezione.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/condotto_sporco_prima_ispezione.png-wvc-sizes" alt="Before HVAC Duct Inspection" className="absolute inset-0 h-full object-cover" style={{
                width: `${100 / position * 100}%`,
                maxWidth: "none"
              }} loading="lazy" decoding="async" draggable={false} onError={handleImageError} />
                </div>

                <div className="absolute top-6 left-6 z-20 pointer-events-none">
                  <span className="inline-block px-4 py-1.5 bg-background/90 backdrop-blur-sm border border-primary/20 font-default font-medium uppercase tracking-wider text-sm text-primary">
                    PRIMA VALIDAZIONE TECNICA
                  </span>
                </div>

                <div className="absolute top-6 right-6 z-20 pointer-events-none">
                  <span className="inline-block px-4 py-1.5 bg-secondary text-primary font-default font-medium uppercase tracking-wider text-sm border border-primary/20">
                    DOPO VALIDAZIONE CERTIFICATA
                  </span>
                </div>

                <div className="absolute top-0 bottom-0 z-30 pointer-events-none" style={{
              left: `${position}%`,
              transform: "translateX(-50%)",
              width: "1px",
              backgroundColor: "var(--primary)"
            }}>
                  <div className="absolute left-1/2 top-1/2 pointer-events-auto cursor-ew-resize flex items-center justify-center border border-primary/30" style={{
                transform: "translate(-50%, -50%)",
                width: "56px",
                height: "56px",
                backgroundColor: "var(--background)"
              }} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} role="slider" aria-label="Drag to compare before and after" aria-valuenow={Math.round(position)} aria-valuemin={0} aria-valuemax={100}>
                    <ArrowLeftRight className="text-primary" style={{
                  width: "24px",
                  height: "24px",
                  strokeWidth: 2
                }} />
                  </div>
                </div>
              </>}
          </div>
        </div>

        <div ref={statsRef} className="mt-16 md:mt-24 border-t border-border transition-all duration-[560ms] ease-out" style={{
        opacity: statsVisible ? 1 : 0,
        transform: statsVisible ? "translateY(0)" : "translateY(20px)",
        transitionTimingFunction: "cubic-bezier(0.2, 0.7, 0.2, 1)"
      }}>
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border">
            {stats.map((stat, i) => <div key={i} data-index={i} className="py-10 px-6 flex flex-col items-center md:items-start gap-2">
                <span className="font-default font-semibold text-primary" style={{
              fontSize: "1.5rem"
            }}>
                  {stat.value}
                </span>
                <span className="font-mono uppercase text-primary tracking-[0.1em]" style={{
              fontSize: "0.75rem"
            }}>
                  {stat.label}
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}